import { useCallback, useEffect, useRef } from "react";
import { useMapsLibrary, useMap } from "@vis.gl/react-google-maps";
import { MapMarker, LassoMode } from "../components/map/map.types";

type UseLassoDrawingProps = {
  markers: MapMarker[];
  drawing: boolean;
  onSelectedMarkersChange?: (markers: MapMarker[], event?: MouseEvent) => void;
  onDrawingComplete?: () => void;
  mode?: LassoMode;
  currentSelection?: MapMarker[];
  onResetSelection?: () => void;
};

const useLassoDrawing = ({
  markers,
  drawing,
  onSelectedMarkersChange,
  onDrawingComplete,
  mode = LassoMode.REPLACE,
  currentSelection = [],
  onResetSelection,
}: UseLassoDrawingProps) => {
  const coreLibrary = useMapsLibrary("core");
  const mapsLibrary = useMapsLibrary("maps");
  const mapInstance = useMap();

  const filterMarkers = useCallback(
    (polyline: google.maps.Polyline) => {
      const polygonPath = polyline.getPath().getArray();
      const polygon = new google.maps.Polygon({ paths: polygonPath });

      const newlySelected = markers.filter((marker) =>
        google.maps.geometry.poly.containsLocation(
          new google.maps.LatLng(marker.latitude ?? 0, marker.longitude ?? 0),
          polygon,
        ),
      );

      let finalSelection: MapMarker[];

      if (mode === LassoMode.ADD) {
        const existingIds = currentSelection.map((marker) => marker.id);
        const uniqueNewSelections = newlySelected.filter(
          (marker) => !existingIds.includes(marker.id),
        );
        finalSelection = [...currentSelection, ...uniqueNewSelections];
      } else if (mode === LassoMode.REMOVE) {
        const newlySelectedIds = newlySelected.map((marker) => marker.id);
        finalSelection = currentSelection.filter(
          (marker) => !newlySelectedIds.includes(marker.id),
        );
      } else {
        onResetSelection?.();
        finalSelection = newlySelected;
      }

      onSelectedMarkersChange?.(
        finalSelection,
        mousePositionRef.current
          ? ({
              clientX: mousePositionRef.current.x,
              clientY: mousePositionRef.current.y,
            } as MouseEvent)
          : undefined,
      );
      polyline.setMap(null);

      onDrawingComplete?.();
    },
    [
      markers,
      onSelectedMarkersChange,
      onDrawingComplete,
      mode,
      currentSelection,
      onResetSelection,
    ],
  );

  const isMouseDownRef = useRef<boolean>(false);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!mapInstance || !coreLibrary || !mapsLibrary) return;

    const handleMouseDownListener = (e: MouseEvent) => {
      if (!drawing) return;
      // Capture mouse position for potential context menu positioning
      mousePositionRef.current = { x: e.clientX, y: e.clientY };

      polylineRef.current = new mapsLibrary!.Polyline({
        strokeColor: "#000",
        strokeOpacity: 1,
        strokeWeight: 1.5,
      });
      polylineRef.current.setMap(mapInstance);
      isMouseDownRef.current = true;
    };

    const handleMouseUpListener = (e: MouseEvent) => {
      if (!drawing) return;
      // Update mouse position for context menu positioning
      mousePositionRef.current = { x: e.clientX, y: e.clientY };

      const path = polylineRef.current!.getPath().getArray();
      if (path.length > 0) {
        path.push(path[0]); // Ensure the polyline is closed
        polylineRef.current!.setPath(path);
        filterMarkers?.(polylineRef.current!);
      }
      isMouseDownRef.current = false;
    };

    const handleMapMouseMoveListener = (e: google.maps.MapMouseEvent) => {
      if (!drawing || !isMouseDownRef.current || !e.latLng) {
        return;
      }
      const path = polylineRef.current!.getPath();
      path.push(e.latLng);
    };

    const mapContainer = mapInstance.getDiv();
    mapInstance.setOptions({
      // To disable dragging
      gestureHandling: drawing ? "none" : "cooperative",
    });

    mapContainer.addEventListener("mousedown", handleMouseDownListener);
    mapContainer.addEventListener("mouseup", handleMouseUpListener);

    coreLibrary.event.addListener(
      mapInstance,
      "mousemove",
      handleMapMouseMoveListener,
    );

    return () => {
      coreLibrary.event.clearListeners(mapInstance, "mousemove");
      mapContainer.removeEventListener("mousedown", handleMouseDownListener);
      mapContainer.removeEventListener("mouseup", handleMouseUpListener);
    };
  }, [mapInstance, coreLibrary, mapsLibrary, drawing, markers]);
};

export default useLassoDrawing;
